import React, { useState } from 'react';
import {
    Container, Typography, Button, TextField, Box, Grid, IconButton,
    Card, CardMedia, CircularProgress, FormHelperText, Checkbox, FormControlLabel
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AddPhotoAlternate, Delete } from '@mui/icons-material';
import { DefaultPage } from '../../../layouts/default-page';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import { useAddNewsMutation } from '../../../services/updates.query';
import CustomConfirmDialog from '../../../layouts/confirm_dialogue';
import { Notify } from '../../../layouts/notify';

const MAX_IMAGES = 4;

const validationSchema = Yup.object().shape({
    title: Yup.string().required('Title is required'),
    content: Yup.string().required('Content is required'),
    date: Yup.date().required('Date is required'),
    images: Yup.array()
        .min(1, 'At least 1 image is required')
        .max(MAX_IMAGES, `Maximum of ${MAX_IMAGES} images allowed`),
    hasLink: Yup.boolean(),
    linkAlias: Yup.string().when('hasLink', {
        is: (val: boolean) => val === true,
        then: (schema) => schema.required('Display Text is required'),
        otherwise: (schema) => schema.notRequired(),
    }),
    linkUrl: Yup.string().when('hasLink', {
        is: (val: boolean) => val === true,
        then: (schema) => schema.required('Link URL is required').url('Invalid URL format'),
        otherwise: (schema) => schema.notRequired(),
    }),
});

const AddNewsPage: React.FC = () => {
    const [images, setImages] = useState<File[]>([]);
    const [loadingImages, setLoadingImages] = useState(false);
    const [imageError, setImageError] = useState('');
    const [addNews, { isLoading }] = useAddNewsMutation();
    const [fileInputKey, setFileInputKey] = useState(0);

    // Confirm Dialog State
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingSubmit, setPendingSubmit] = useState<any>(null);

    const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>, setFieldValue: (field: string, value: any) => void) => {
        if (event.target.files) {
            setLoadingImages(true);
            setImageError('');

            const selectedFiles = Array.from(event.target.files);

            if (images.length + selectedFiles.length > MAX_IMAGES) {
                setImageError(`You can only upload up to ${MAX_IMAGES} images.`);
                setLoadingImages(false);
                return;
            }

            const updatedImages = [...images, ...selectedFiles];
            setImages(updatedImages);
            setFieldValue('images', updatedImages);
            setLoadingImages(false);
        }
    };

    const handleRemoveImage = (index: number, setFieldValue: (field: string, value: any) => void) => {
        const updatedImages = images.filter((_, i) => i !== index);
        setImages(updatedImages);
        setFieldValue('images', updatedImages);
        setImageError('');

        if (updatedImages.length === 0) {
            setFileInputKey(prev => prev + 1);
        }
    };

    return (
        <DefaultPage title="Add News">
            <Container maxWidth="md">
                <Formik
                    initialValues={{
                        title: '',
                        content: '',
                        date: dayjs().toDate(),
                        images: [],
                        hasLink: false,
                        linkAlias: '',
                        linkUrl: '',
                    }}
                    validationSchema={validationSchema}
                    onSubmit={(values) => {
                        setPendingSubmit(values);
                        setConfirmOpen(true);
                    }}
                >
                    {({ values, setFieldValue, errors, touched, resetForm }) => (
                        <>
                            <Form noValidate>
                                <Grid container spacing={3}>
                                    {/* Title */}
                                    <Grid item xs={12}>
                                        <Field
                                            as={TextField}
                                            name="title"
                                            label="Title"
                                            variant="outlined"
                                            fullWidth
                                            error={touched.title && Boolean(errors.title)}
                                            helperText={<ErrorMessage name="title" />}
                                        />
                                    </Grid>

                                    {/* Content */}
                                    <Grid item xs={12}>
                                        <Field
                                            as={TextField}
                                            name="content"
                                            label="Content"
                                            variant="outlined"
                                            multiline
                                            rows={6}
                                            fullWidth
                                            error={touched.content && Boolean(errors.content)}
                                            helperText={<ErrorMessage name="content" />}
                                        />
                                    </Grid>

                                    {/* Date Picker */}
                                    <Grid item xs={12} sm={6}>
                                        <DatePicker
                                            label="Date"
                                            value={dayjs(values.date)}
                                            onChange={(newValue) => setFieldValue('date', newValue?.toDate())}
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    error: touched.date && Boolean(errors.date),
                                                    helperText: touched.date && typeof errors.date === 'string' ? errors.date : ''
                                                }
                                            }}
                                        />
                                    </Grid>

                                    {/* Image Upload */}
                                    <Grid item xs={12}>
                                        <Typography variant="h6" gutterBottom>Image Attachments (Max: 4)</Typography>
                                        <Typography variant="body2" sx={{ mb: 1, color: 'error.main' }}>
                                            <strong>Note:</strong> The first image will be considered as the <strong>Main Image</strong>.
                                        </Typography>

                                        <Button
                                            variant="contained"
                                            component="label"
                                            startIcon={<AddPhotoAlternate />}
                                            disabled={images.length >= MAX_IMAGES || loadingImages}
                                            sx={{ mb: 1 }}
                                        >
                                            {loadingImages ? <CircularProgress size={24} color="inherit" /> : 'Upload Images'}
                                            <input
                                                key={fileInputKey}
                                                type="file"
                                                hidden
                                                accept="image/*"
                                                multiple
                                                onChange={(e) => handleImageChange(e, setFieldValue)}
                                            />
                                        </Button>

                                        {(imageError || (touched.images && errors.images)) && (
                                            <FormHelperText error sx={{ mb: 2 }}>
                                                {imageError || errors.images}
                                            </FormHelperText>
                                        )}

                                        <Grid container spacing={2}>
                                            {images.map((image, index) => (
                                                <Grid item xs={6} sm={3} key={index}>
                                                    <Card sx={{ position: 'relative', borderRadius: 0.5 }}>
                                                        <CardMedia
                                                            component="img"
                                                            height="140"
                                                            image={URL.createObjectURL(image)}
                                                            alt={`attachment-${index}`}
                                                        />
                                                        <IconButton
                                                            onClick={() => handleRemoveImage(index, setFieldValue)}
                                                            color="error"
                                                            sx={{
                                                                position: 'absolute',
                                                                top: 5,
                                                                right: 5,
                                                                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(255, 0, 0, 0.2)',
                                                                },
                                                                borderRadius: 1.5,
                                                            }}
                                                        >
                                                            <Delete />
                                                        </IconButton>
                                                    </Card>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Grid>

                                    {/* Checkbox for Link */}
                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={values.hasLink}
                                                    onChange={(e) => setFieldValue('hasLink', e.target.checked)}
                                                />
                                            }
                                            label="Add Link Attachment"
                                        />
                                    </Grid>

                                    {/* Link Attachment */}
                                    {values.hasLink && (
                                        <Grid item xs={12}>
                                            <Typography variant="h6" gutterBottom>Link Attachment (Max: 1)</Typography>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={6}>
                                                    <Field
                                                        as={TextField}
                                                        name="linkAlias"
                                                        label="Display Text (e.g. Read More)"
                                                        variant="outlined"
                                                        fullWidth
                                                        error={touched.linkAlias && Boolean(errors.linkAlias)}
                                                        helperText={<ErrorMessage name="linkAlias" />}
                                                    />
                                                </Grid>

                                                <Grid item xs={12} sm={6}>
                                                    <Field
                                                        as={TextField}
                                                        name="linkUrl"
                                                        label="Link URL"
                                                        variant="outlined"
                                                        fullWidth
                                                        error={touched.linkUrl && Boolean(errors.linkUrl)}
                                                        helperText={<ErrorMessage name="linkUrl" />}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    )}

                                    {/* Submit Button */}
                                    <Grid item xs={12}>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            color="success"
                                            size="large"
                                            startIcon={isLoading && <CircularProgress size={20} color="inherit" />}
                                            disabled={isLoading}
                                        >
                                            {isLoading ? 'Submitting...' : 'Submit'}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Form>

                            {/* Confirm Dialog */}
                            <CustomConfirmDialog
                                open={confirmOpen}
                                onClose={() => setConfirmOpen(false)}
                                onConfirm={async () => {
                                    try {
                                        await addNews({
                                            title: pendingSubmit.title,
                                            content: pendingSubmit.content,
                                            date: pendingSubmit.date,
                                            images: images,
                                            linkAlias: pendingSubmit.hasLink ? pendingSubmit.linkAlias : '',
                                            linkUrl: pendingSubmit.hasLink ? pendingSubmit.linkUrl : '',
                                        }).unwrap();

                                        Notify("success", "News Added Successfully");
                                        resetForm();
                                        setImages([]);
                                        setConfirmOpen(false);
                                    } catch (error) {
                                        console.error('Error adding news:', error);
                                    }
                                }}
                                loading={isLoading}
                                title="Confirm Submission"
                                description="Are you sure you want to submit this news?"
                                color="success"
                            />
                        </>
                    )}
                </Formik>
            </Container>
        </DefaultPage>
    );
};

export default AddNewsPage;
